/**
 * Reading the customer's configuration off the script tag.
 *
 * The whole integration has to be a single line the customer pastes. Every
 * comparable product works that way: Zendesk carries the key in a query string,
 * Tawk.to in the path, Crisp and Intercom in a global set just before the loader.
 * None of them ask for an API URL — that is compiled in.
 */

/** The tag that loaded this script. */
export function currentScript(doc = document) {
  // document.currentScript is null once we are inside an async callback, so this
  // has to be read while the module body is still executing.
  return doc.currentScript
    || doc.querySelector('script[data-site-key]')
    || doc.querySelector('script[src*="site_key="]');
}

/**
 * The site key, accepted two ways.
 *
 * `data-site-key` is what the documented snippet uses. The query string is a
 * fallback because some tag managers and CMS editors strip unknown data
 * attributes while leaving the src untouched — and a customer whose widget
 * silently never appears is a support ticket we do not want.
 */
export function readSiteKey(script) {
  const fromData = script?.dataset?.siteKey;
  if (fromData) return fromData.trim();

  try {
    const fromQuery = new URL(script.src).searchParams.get('site_key');
    if (fromQuery) return fromQuery.trim();
  } catch {
    // A malformed src is the customer's problem, not a crash of ours.
  }
  return null;
}

/**
 * The API base.
 *
 * Compiled in at build time. `data-api-base` is an override for local and e2e
 * builds only (`__ALLOW_API_OVERRIDE__`, scripts/build.js): a production build
 * compiles it out. Otherwise markup injected into a customer's page — or found
 * by the `currentScript` fallback — could point the widget, and on the
 * platform's own pages the `X-Chatbot-Auth` token, at another host. It is
 * also absent from the snippet we hand customers: asking them for it would make
 * a one-line integration a two-line one with a value they cannot know.
 */
export function readApiBase(script) {
  if (__ALLOW_API_OVERRIDE__ && script?.dataset?.apiBase) return script.dataset.apiBase;
  return __API_BASE__;
}
